import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FormScheduleDService } from './form-schedule-d.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-form-schedule-d',
    templateUrl: './form-schedule-d.component.html',
    styleUrls: ['./form-schedule-d.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormScheduleDComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = 'Schedule D (Form 1040): Capital Gains and Losses - Increase (Decrease) To Taxable Income';

  activeTab = 0;
  selectedTabName = 'Form Entry';

  /** Form Schedule D pivot/form data - populated by loadPivotFormJson */
  formData: any = null;

  /** Entry tab: section data and line headers */
  scheduleDFormEntry: any = {
    section1: { singlerows: [], part1Title: 'Short-Term Capital Gains and Losses—Assets Held One Year or Less', rows: [] },
    section2: { part2Title: 'Long-Term Capital Gains and Losses—Assets Held More Than One Year', rows: [] }
  };
  scheduleDLine1Headers: { key: string; name: string }[] = [];
  scheduleDLine6Headers: { key: string; name: string }[] = [];
  scheduleDLine1HeaderComment: { name: string } = { name: 'Comments' };
  scheduleDLine6HeaderComment: { name: string } = { name: 'Comments' };
  formData_1: any = { addrows: [] };
  formData_6: any = { addrows: [] };
  formData_1_errorMessages: any = { addrows: [] };
  formData_6_errorMessages: any = { addrows: [] };
  scheduleDLine1Records: any[] = [];
  scheduleDLine6Records: any[] = [];
  displayViewValue_1: boolean[] = [];
  displayEditValue_1: boolean[] = [];
  displayViewValueComment_1: boolean[] = [];
  displayEditValueComment_1: boolean[] = [];
  displayViewValue_6: boolean[] = [];
  displayEditValue_6: boolean[] = [];
  displayViewValueComment_6: boolean[] = [];
  displayEditValueComment_6: boolean[] = [];
  removedRows1: any[] = [];
  removedRows6: any[] = [];
  unUsedCapLossCarryOver: number | string = '';
  schdDSum: number = 0;
  unallocatedAmount: number = 0;

  /** View tab: section data */
  scheduleDFormView: any = {
    section1: { part1Title: 'Short-Term Capital Gains and Losses—Assets Held One Year or Less', tableHeaders: [], rows: [] },
    section2: { part1Title: 'Long-Term Capital Gains and Losses—Assets Held More Than One Year', rows: [] },
    section3: { part3Title: 'Summary', rows: [] }
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private formScheduleDService: FormScheduleDService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    this.loadData();
  }

  private loadData() {
    const gs = this.globalService?.globalParams ?? this.globalService;
    const params: any = {
      action_id: 372,
      tax_year: this.rowData?.TAX_YEAR ?? this.filterParams?.tax_year ?? gs?.tax_year,
      scenario: this.rowData?.SCENARIO ?? this.filterParams?.scenario ?? gs?.scenario,
      jcd_key: this.rowData?.JCD_KEY ?? this.filterParams?.jcd_key ?? gs?.jcd_key ?? 250,
      combination_key: this.rowData?.COMBINATION_KEY ?? this.filterParams?.combination_key
    };

    this.formScheduleDService.loadPivotFormJson(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.formData = data;
        this.processFormData(data);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (err: any) => {
        console.error('Error loading Form Schedule D data:', err);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  /** Map loaded form data into scheduleDFormEntry and scheduleDFormView for Entry/View tabs */
  processFormData(data: any): void {
    if (!data) return;
    const irsForm = data?.irsForm || data?.form || data;
    const formItemList = irsForm?.formItemList || irsForm?.items || [];
    if (this.scheduleDLine1Headers.length === 0) {
      this.scheduleDLine1Headers = [
        { key: '1', name: 'Description' }, { key: '2', name: 'Date acquired' }, { key: '3', name: 'Date sold' },
        { key: '4', name: 'Proceeds' }, { key: '5', name: 'Cost' }, { key: '6', name: 'Transaction type' },
        { key: '7', name: 'Checkbox 1' }, { key: '8', name: 'Checkbox 2' }, { key: '9', name: 'Checkbox 3' }, { key: '10', name: 'Checkbox 4' }, { key: '11', name: 'Gain/Loss' }
      ];
      this.scheduleDLine6Headers = [...this.scheduleDLine1Headers];
    }
    if (!this.scheduleDFormEntry.section1.singlerows.length) {
      this.scheduleDFormEntry.section1.singlerows = [this.rowData?.TB_AMT ?? 0];
    }
    if (Array.isArray(formItemList) && formItemList.length > 0) {
      this.scheduleDFormView.section1.tableHeaders = this.scheduleDFormView.section1.tableHeaders.length ? this.scheduleDFormView.section1.tableHeaders : ['No.', 'Description', 'Proceeds', 'Cost', 'Adjustment', 'Gain or (Loss)'];
      if (!this.scheduleDFormView.section1.rows.length) {
        this.scheduleDFormView.section1.rows = [
          { num: '1', description: 'Short-term totals', proceeds: { show: true }, cost: { show: true }, adjustment: { show: true }, gain: { show: true } },
          { num: '7', description: 'Net short-term capital gain or (loss)', gain: { show: true } }
        ];
      }
      if (!this.scheduleDFormView.section2.rows.length) {
        this.scheduleDFormView.section2.rows = [
          { num: '8', description: 'Long-term totals', proceeds: { show: true }, cost: { show: true }, adjustment: { show: true }, gain: { show: true } },
          { num: '15', description: 'Net long-term capital gain or (loss)', gain: { show: true } }
        ];
      }
      if (!this.scheduleDFormView.section3.rows.length) {
        this.scheduleDFormView.section3.rows = [
          { num: '16', description: 'Combine lines 7 and 15', gain: { show: true } }
        ];
      }
      const itemList = irsForm.formItemList || [];
      if (itemList[3]?.dataRowList?.length) {
        this.scheduleDLine1Records = itemList[3].dataRowList;
        this.displayViewValue_1 = this.scheduleDLine1Records.map(() => true);
        this.displayEditValue_1 = this.scheduleDLine1Records.map(() => false);
        this.displayViewValueComment_1 = this.scheduleDLine1Records.map(() => true);
        this.displayEditValueComment_1 = this.scheduleDLine1Records.map(() => false);
      }
      if (itemList[6]?.dataRowList?.length) {
        this.scheduleDLine6Records = itemList[6].dataRowList;
        this.displayViewValue_6 = this.scheduleDLine6Records.map(() => true);
        this.displayEditValue_6 = this.scheduleDLine6Records.map(() => false);
        this.displayViewValueComment_6 = this.scheduleDLine6Records.map(() => true);
        this.displayEditValueComment_6 = this.scheduleDLine6Records.map(() => false);
      }
      if (itemList[4]?.dataRowList?.[0]?.dataCellList?.[2]?.value != null) {
        this.unUsedCapLossCarryOver = itemList[4].dataRowList[0].dataCellList[2].value;
      }
    }
  }

  sectionAdd(section: number): void {
    if (section === 1) {
      const newRow = Array.from({ length: 12 }, (_, i) => ({ Attribute_Value: i === 5 ? 'Perm' : (i >= 6 && i <= 9 ? 'N' : '') }));
      this.formData_1.addrows.push(newRow);
      this.formData_1_errorMessages.addrows.push(Array.from({ length: 12 }, () => ({ column_error: '' })));
    } else if (section === 6) {
      const newRow = Array.from({ length: 12 }, (_, i) => ({ Attribute_Value: i === 5 ? 'Perm' : (i >= 6 && i <= 9 ? 'N' : '') }));
      this.formData_6.addrows.push(newRow);
      this.formData_6_errorMessages.addrows.push(Array.from({ length: 12 }, () => ({ column_error: '' })));
    }
    this.cdr.detectChanges();
  }

  sectionRemove(section: number, index: number): void {
    if (section === 1) {
      this.formData_1.addrows.splice(index, 1);
      this.formData_1_errorMessages.addrows.splice(index, 1);
    } else if (section === 6) {
      this.formData_6.addrows.splice(index, 1);
      this.formData_6_errorMessages.addrows.splice(index, 1);
    }
    this.cdr.detectChanges();
  }

  errorChange(section: number, rowIndex: number, colIndex: number): void {
    if (section === 1 && this.formData_1_errorMessages.addrows[rowIndex]?.[colIndex]) {
      this.formData_1_errorMessages.addrows[rowIndex][colIndex].column_error = '';
    }
    if (section === 6 && this.formData_6_errorMessages.addrows[rowIndex]?.[colIndex]) {
      this.formData_6_errorMessages.addrows[rowIndex][colIndex].column_error = '';
    }
    this.cdr.detectChanges();
  }

  propChange(section: number, colIndex: number, rowIndex: number): void {
    this.cdr.detectChanges();
  }

  setGainLoss(section: number, isRecord: boolean, index: number): void {
    this.cdr.detectChanges();
  }

  editExistingRecord(section: number, index: number, objectId: any): void {
    if (section === 1) {
      this.displayViewValue_1[index] = false;
      this.displayEditValue_1[index] = true;
      this.displayViewValueComment_1[index] = false;
      this.displayEditValueComment_1[index] = true;
    } else if (section === 6) {
      this.displayViewValue_6[index] = false;
      this.displayEditValue_6[index] = true;
      this.displayViewValueComment_6[index] = false;
      this.displayEditValueComment_6[index] = true;
    }
    this.cdr.detectChanges();
  }

  removeExistingRecord(section: number, index: number, objectId: any): void {
    const rec = section === 1 ? this.scheduleDLine1Records[index] : this.scheduleDLine6Records[index];
    const removeObj = {
      tax_year: this.rowData?.TAX_YEAR,
      trans_type_key: 8,
      combination_key: this.rowData?.COMBINATION_KEY,
      group_obj_key: this.rowData?.GROUP_OBJ_KEY,
      form_key: 2,
      line_no: section,
      occurence: rec?.rowDimensions?.occurence || rec?.rowDimensions?.occurrence || 1,
      trans_dtls_key: rec?.rowDimensions?.object_id || objectId,
      trans_status: 'D'
    };
    if (section === 1) {
      this.removedRows1.push(removeObj);
      this.scheduleDLine1Records.splice(index, 1);
      this.displayViewValue_1.splice(index, 1);
      this.displayEditValue_1.splice(index, 1);
    } else if (section === 6) {
      this.removedRows6.push(removeObj);
      this.scheduleDLine6Records.splice(index, 1);
      this.displayViewValue_6.splice(index, 1);
      this.displayEditValue_6.splice(index, 1);
    }
    this.cdr.detectChanges();
  }

  adjustGainLoss(): void {
    this.cdr.detectChanges();
  }

  line4Change(): void {
    this.cdr.detectChanges();
  }

  get displayLine4Edit(): boolean {
    return true;
  }

  get displayLine4View(): boolean {
    return false;
  }

  onTabChange(index: number) {
    this.activeTab = index;
    this.selectedTabName = index === 0 ? 'Form Entry' : 'Form View';
    this.cdr.detectChanges();
  }

  save() {
    if (this.crudLoading) return;
    this.crudLoading = true;
    this.cdr.detectChanges();
    const line1Keys = [31, 32, 33, 34, 35, 37, 253, 38, 39, 40, 36, 41];
    const line6Keys = [53, 54, 55, 56, 57, 59, 264, 60, 61, 62, 58, 63];
    const baseRow = {
      tax_year: this.rowData?.TAX_YEAR,
      trans_type_key: 8,
      combination_key: this.rowData?.COMBINATION_KEY,
      group_obj_key: this.rowData?.GROUP_OBJ_KEY,
      form_key: 2,
      trans_status: 'A'
    };

    const mapAddedRows = (rows: any[], lineNo: number, attribKeys: number[]) =>
      (rows || []).map((row: any, idx: number) =>
        attribKeys.map((attribKey, colIdx) => ({
          ...baseRow,
          line_no: lineNo,
          occurence: idx + 1,
          line_attrib_key: attribKey,
          Attribute_Value: row?.[colIdx]?.Attribute_Value,
          is_dirty: 'Y',
          trans_dtls_key: null
        }))
      );

    const mapEditedRecords = (records: any[], lineNo: number, attribKeys: number[]) =>
      (records || []).map((rec: any) => {
        const cells = rec?.dataCellList || [];
        return attribKeys.map((attribKey, idx) => ({
          ...baseRow,
          line_no: lineNo,
          occurence: rec?.rowDimensions?.occurence || rec?.rowDimensions?.occurrence || 1,
          line_attrib_key: attribKey,
          Attribute_Value: cells?.[idx + 1]?.value ?? null,
          is_dirty: 'Y',
          trans_dtls_key: rec?.rowDimensions?.object_id || null
        }));
      });

    const line4Edit = [[{
      ...baseRow,
      line_no: 4,
      occurence: 1,
      line_attrib_key: 47,
      Attribute_Value: this.unUsedCapLossCarryOver,
      is_dirty: 'Y',
      trans_dtls_key: null
    }]];

    const addrows = [
      ...mapAddedRows(this.formData_1?.addrows, 1, line1Keys),
      ...mapAddedRows(this.formData_6?.addrows, 6, line6Keys)
    ];
    const editrows = [
      ...mapEditedRecords(this.scheduleDLine1Records, 1, line1Keys),
      ...mapEditedRecords(this.scheduleDLine6Records, 6, line6Keys),
      ...line4Edit
    ];
    const removerows = [...this.removedRows1, ...this.removedRows6];
    const mainData = [{ addrows, editrows, removerows }];

    if (addrows.length === 0 && editrows.length === 0 && removerows.length === 0) {
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar('No changes to save', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    const settingsObj = [{
      sso_id: this.globalService?.sso_id || this.globalService?.globalParams?.sso_id || '',
      screen_key: this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '',
      combination_key: this.rowData?.COMBINATION_KEY,
      form_name: 'ScheduleD'
    }];

    this.formScheduleDService.saveScheduleDForm(
      this.GENERAL_CONFIG.base_url,
      mainData,
      settingsObj,
      this.globalService?.globalParams || this.globalService || {},
      this.filterParams || {},
      'Form Schedule D Save'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.loadData();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
